import { type GetListParams, type QueryFunctionContext } from "react-admin";

import { CLIENT_SEARCH_SOURCE } from "@/constants/filters";
import { LIST_PARAMS_LS_KEY } from "@/constants/localStorage";

import { filterByString } from "./filters";
import { getFullListQueryData } from "./query";
import { sortByField } from "./sorters";

type ListParams = GetListParams & QueryFunctionContext;

type FilterRecord = Record<string, unknown>;

type ResourceCache = Record<string, Record<string, unknown>>;

export const setListParams = (
  resource: string,
  params: ListParams,
  cache: ResourceCache,
) => {
  const { pagination, sort } = params;
  const filter = params.filter as FilterRecord;
  const listParams = {
    resource,
    pagination,
    filter,
    sort,
  };

  cache[LIST_PARAMS_LS_KEY] = listParams;
};

type ListParamsFromLS = ListParams & {
  resource: string;
};

export const getListParams = (cache: ResourceCache): ListParamsFromLS | null =>
  (cache[LIST_PARAMS_LS_KEY] ?? null) as unknown as ListParamsFromLS | null;

export const getIsParamsEqual = (
  params: ListParams,
  resource: string,
  cache: ResourceCache,
) => {
  const lsParams = getListParams(cache);

  if (!lsParams || resource !== lsParams.resource) {
    return {
      isOtherFiltersEqual: false,
      isParamsEqual: false,
      newParams: params,
    };
  }

  const { pagination, sort } = params;
  const { pagination: lsPagination, sort: lsSort } = lsParams;
  const typedFilter = params.filter as FilterRecord;
  const typedLsFilter = lsParams.filter as FilterRecord;

  const isParamsEqual =
    JSON.stringify({
      pagination,
      filter: typedFilter,
      sort,
    }) ===
    JSON.stringify({
      pagination: lsPagination,
      filter: typedLsFilter,
      sort: lsSort,
    });

  const isClientFilterEqual =
    typedFilter[CLIENT_SEARCH_SOURCE] === typedLsFilter[CLIENT_SEARCH_SOURCE];
  const isSortEqual =
    sort?.field === lsSort?.field && sort?.order === lsSort?.order;
  const isOtherFiltersEqual = !Object.entries(typedFilter).some(
    ([key, value]) =>
      key !== CLIENT_SEARCH_SOURCE && value !== typedLsFilter[key],
  );

  const newParams =
    isSortEqual && isClientFilterEqual
      ? params
      : { ...params, pagination: lsParams.pagination || params.pagination };

  return {
    isOtherFiltersEqual,
    isParamsEqual,
    newParams,
  };
};

export const checkAndSetListParams = (
  params: ListParams,
  resource: string,
  cache: ResourceCache,
) => {
  const equivalence = getIsParamsEqual(params, resource, cache);

  if (!equivalence.isParamsEqual) {
    setListParams(resource, equivalence.newParams, cache);
  }

  return equivalence;
};

export const getLocalFilteredSortedList = <T>(
  fetchFunc: (params: ListParams) => Promise<{ data: T[]; total: number }>,
  getCachedData: (
    resource: string,
    params: ListParams,
  ) => { data: T[]; total: number } | undefined = getFullListQueryData,
) => {
  const cache = {};

  return async (params: ListParams, resource: string) => {
    const { isParamsEqual, isOtherFiltersEqual, newParams } =
      checkAndSetListParams(params, resource, cache);
    const fullListData = getCachedData(resource, newParams);

    const { sort } = newParams;
    const newFilter = newParams.filter as FilterRecord;
    const filterStringRaw = newFilter[CLIENT_SEARCH_SOURCE];
    const filterString: string | undefined =
      typeof filterStringRaw === "string" ? filterStringRaw : undefined;

    const { field: sortField, order: sortOrder } = sort || {};

    let currentData = fullListData;

    if (!fullListData || isParamsEqual || !isOtherFiltersEqual) {
      currentData = await fetchFunc(newParams);
    }
    const filteredData =
      filterString && currentData
        ? filterByString(currentData.data, filterString)
        : currentData?.data;

    const sortedData = sortField
      ? sortByField(filteredData || [], sortField as keyof T, sortOrder)
      : filteredData;

    return {
      data: sortedData || [],
      total: filterString ? (filteredData?.length ?? 0) : currentData?.total,
    };
  };
};
